<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Chốt sổ hoa hồng'">
          <template v-slot:preview>
            <!-- Chọn kỳ + phạm vi -->
            <b-form class="mb-5">
              <b-row>
                <b-col md="2">
                  <label class="form-label">Ngành</label>
                  <b-form-select
                    size="sm"
                    v-model="filter.type"
                    :options="typeOptions"
                    value-field="id"
                    text-field="name"
                    @change="onTypeChange"
                  ></b-form-select>
                </b-col>
                <b-col md="3" v-if="isStore">
                  <label class="form-label">Cửa hàng</label>
                  <Autosuggest
                    :key="'store-' + componentKey"
                    :model="filter.storeName"
                    :suggestions="filteredStores"
                    placeholder="Chọn cửa hàng"
                    :limit="10"
                    @select="onSelectStore($event)"
                    @change="onStoreChange"
                    suggestionName="suggestionName"
                  />
                </b-col>
                <b-col md="3" v-else>
                  <label class="form-label">Bộ phận</label>
                  <b-form-select
                    size="sm"
                    v-model="filter.departmentId"
                    :options="departments"
                    value-field="id"
                    text-field="name"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null"
                        >-- Chọn bộ phận --</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                </b-col>
                <b-col md="2">
                  <label class="form-label">Từ ngày</label>
                  <date-picker
                    placeholder="Từ ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.fromDate"
                  ></date-picker>
                </b-col>
                <b-col md="2">
                  <label class="form-label">Đến ngày</label>
                  <date-picker
                    placeholder="Đến ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.toDate"
                  ></date-picker>
                </b-col>
                <b-col md="3" class="d-flex align-items-end">
                  <b-button
                    variant="primary"
                    size="sm"
                    class="mr-2"
                    :disabled="onLoading"
                    @click="checkStatus"
                  >
                    <i class="fas fa-search mr-1"></i>Kiểm tra kỳ
                  </b-button>
                </b-col>
              </b-row>
            </b-form>

            <!-- Trạng thái kỳ -->
            <div v-if="status" class="mb-5">
              <div
                class="status-box"
                :class="status.isSettled ? 'status-box--locked' : 'status-box--open'"
              >
                <div>
                  <div class="status-box__title">
                    <i
                      class="mr-2"
                      :class="status.isSettled ? 'fas fa-lock' : 'fas fa-lock-open'"
                    ></i>
                    {{ status.isSettled ? 'Kỳ đã chốt sổ' : 'Kỳ chưa chốt' }}
                  </div>
                  <div class="status-box__meta" v-if="status.isSettled">
                    {{ status.settlement.employeeCount }} nhân sự ·
                    Tổng
                    <strong>{{ displayAmount(status.settlement.totalAmount) }}</strong>
                    · Chốt lúc
                    {{ displayDate(status.settlement.settledAt) }}
                    <span v-if="status.settlement.settledByName">
                      bởi {{ status.settlement.settledByName }}
                    </span>
                  </div>
                  <div class="status-box__meta" v-else>
                    Kỳ này chưa được chốt — dữ liệu vẫn sửa được bình thường.
                  </div>
                </div>
                <div>
                  <b-button
                    v-if="!status.isSettled"
                    variant="primary"
                    size="sm"
                    :disabled="isWorking"
                    @click="confirmSettle"
                  >
                    <i class="fas fa-lock mr-1"></i>Chốt sổ
                  </b-button>
                  <template v-else>
                    <b-button
                      variant="secondary"
                      size="sm"
                      class="mr-2"
                      @click="openStatement(status.settlement.id)"
                    >
                      <i class="fas fa-file-invoice mr-1"></i>Bảng kê
                    </b-button>
                    <b-button
                      variant="primary"
                      size="sm"
                      class="mr-2"
                      :disabled="isWorking"
                      @click="confirmSettle"
                    >
                      <i class="fas fa-redo mr-1"></i>Chốt lại
                    </b-button>
                    <b-button
                      variant="danger"
                      size="sm"
                      :disabled="isWorking"
                      @click="confirmRevoke(status.settlement.id)"
                    >
                      <i class="fas fa-lock-open mr-1"></i>Huỷ chốt
                    </b-button>
                  </template>
                </div>
              </div>
            </div>

            <!-- Lịch sử các kỳ đã chốt -->
            <p class="font-weight-bold mb-2">Lịch sử chốt sổ:</p>
            <b-table
              class="table-bordered table-hover"
              :fields="historyFields"
              :items="history"
              :busy="onLoadingHistory"
              small
              show-empty
              empty-text="Chưa có kỳ nào được chốt"
            >
              <template v-slot:table-busy>
                <vcl-table :speed="5" :animate="true" :columns="6"></vcl-table>
              </template>
              <template v-slot:cell(scope)="row">
                {{ row.item.type === 3 ? 'CR' : 'Cửa hàng' }}
                <span class="text-muted">
                  {{ row.item.storeId || row.item.departmentId }}
                </span>
              </template>
              <template v-slot:cell(period)="row">
                {{ displayDate(row.item.fromDate) }} —
                {{ displayDate(row.item.toDate) }}
              </template>
              <template v-slot:cell(totalAmount)="row">
                <span :class="amountClass(row.item.totalAmount)">
                  {{ displayAmount(row.item.totalAmount) }}
                </span>
              </template>
              <template v-slot:cell(settledAt)="row">
                {{ displayDate(row.item.settledAt) }}
              </template>
              <template v-slot:cell(actions)="row">
                <b-button
                  variant="link"
                  size="sm"
                  class="p-0"
                  @click="openStatement(row.item.id)"
                >
                  Bảng kê
                </b-button>
              </template>
            </b-table>
          </template>
        </KTCodePreview>
      </div>
    </div>

    <!-- Bảng kê trình ký -->
    <b-modal
      v-model="statementVisible"
      title="Bảng kê chốt hoa hồng"
      size="xl"
      ok-only
      ok-title="Đóng"
    >
      <vcl-table v-if="statementLoading" :columns="8"></vcl-table>
      <div v-else-if="statement">
        <div class="statement-head mb-3">
          <div>
            Kỳ:
            <strong>
              {{ displayDate(statement.header.fromDate) }} —
              {{ displayDate(statement.header.toDate) }}
            </strong>
          </div>
          <div v-if="statement.header.storeName">
            Cửa hàng: <strong>{{ statement.header.storeName }}</strong>
          </div>
          <div v-else-if="statement.header.departmentId">
            Bộ phận: <strong>{{ statement.header.departmentId }}</strong>
          </div>
          <div>
            Ngày chốt:
            <strong>{{ displayDate(statement.header.settledAt) }}</strong>
            <span v-if="statement.header.settledByName">
              · Người chốt: <strong>{{ statement.header.settledByName }}</strong>
            </span>
          </div>
        </div>

        <b-alert v-if="statement.hiddenZeroCount" show variant="light" class="hint">
          {{ statement.hiddenZeroCount }} dòng có Tổng thưởng bằng 0 không đưa vào
          bảng kê (vẫn được lưu đầy đủ trong bảng chốt sổ).
        </b-alert>

        <b-table
          class="table-bordered table-hover"
          :fields="statementFields"
          :items="statement.rows"
          small
          show-empty
          empty-text="Không có dòng nào"
        >
          <template v-slot:cell(count)="row">{{ row.index + 1 }}</template>
          <template v-slot:cell(commissionAmount)="row">
            {{ displayAmount(row.item.commissionAmount) }}
          </template>
          <template v-slot:cell(adhocAmount)="row">
            {{ displayAmount(row.item.adhocAmount) }}
          </template>
          <template v-slot:cell(bonusAmount)="row">
            {{ displayAmount(row.item.bonusAmount) }}
          </template>
          <template v-slot:cell(minusAmount)="row">
            {{ displayAmount(row.item.minusAmount) }}
          </template>
          <template v-slot:cell(totalAmount)="row">
            <span class="font-weight-bold" :class="amountClass(row.item.totalAmount)">
              {{ displayAmount(row.item.totalAmount) }}
            </span>
          </template>
          <template v-slot:custom-foot>
            <tr class="statement-total">
              <td colspan="4" class="text-right">TỔNG CỘNG</td>
              <td class="text-right">
                {{ displayAmount(statement.totals.commissionAmount) }}
              </td>
              <td class="text-right">
                {{ displayAmount(statement.totals.adhocAmount) }}
              </td>
              <td class="text-right">
                {{ displayAmount(statement.totals.bonusAmount) }}
              </td>
              <td class="text-right">
                {{ displayAmount(statement.totals.minusAmount) }}
              </td>
              <td class="text-right" :class="amountClass(statement.totals.totalAmount)">
                {{ displayAmount(statement.totals.totalAmount) }}
              </td>
            </tr>
          </template>
        </b-table>
      </div>
    </b-modal>
  </div>
</template>

<script>
/**
 * CHỐT SỔ HOA HỒNG — SRS F-08 / §7.10.
 *
 * Chốt sổ là xác nhận TIỀN ĐÃ TRẢ cho kỳ. Chốt xong kỳ bị khoá: bút toán
 * cộng/trừ thuộc kỳ không sửa/xoá/thêm được. Muốn sửa: huỷ chốt → sửa → chốt lại.
 *
 * Bảng kê (để trình ký) BỎ dòng Tổng thưởng = 0 nhưng GIỮ dòng âm — khác bảng
 * chốt sổ vốn lưu hết. Số dòng bị ẩn được nói rõ để người đọc không thắc mắc.
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import datePicker from 'vue-bootstrap-datetimepicker';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import Swal from 'sweetalert2';
import moment from 'moment';
import { VclTable } from 'vue-content-loading';
import { makeToastSuccess, makeToastFaile, removeAccents } from '@/utils/common';
import commissionReportMixin, {
  DATE_INPUT_FORMAT,
  thStyle,
} from './commissionReport.mixin';

const TYPE_STORE = 1;
const TYPE_CR = 3;

export default {
  components: { KTCodePreview, Autosuggest, VclTable, datePicker },
  mixins: [commissionReportMixin],
  data() {
    return {
      componentKey: 0,
      onLoading: false,
      onLoadingHistory: false,
      isWorking: false,
      status: null,
      history: [],
      stores: [],
      filteredStores: [],
      departments: [],
      filter: {
        type: TYPE_STORE,
        storeId: null,
        storeName: '',
        departmentId: null,
        fromDate: moment().startOf('month').format(DATE_INPUT_FORMAT),
        toDate: moment().endOf('month').format(DATE_INPUT_FORMAT),
      },
      typeOptions: [
        { id: TYPE_STORE, name: 'Cửa hàng' },
        { id: TYPE_CR, name: 'CR' },
      ],
      statementVisible: false,
      statementLoading: false,
      statement: null,

      historyFields: [
        { key: 'scope', label: 'Phạm vi', thStyle: thStyle('18%') },
        { key: 'period', label: 'Kỳ', thStyle: thStyle('24%') },
        {
          key: 'employeeCount',
          label: 'Số NV',
          thStyle: thStyle('10%'),
          tdClass: 'text-center',
        },
        {
          key: 'totalAmount',
          label: 'Tổng thưởng',
          thStyle: thStyle('18%'),
          tdClass: 'text-right',
        },
        { key: 'settledAt', label: 'Ngày chốt', thStyle: thStyle('20%') },
        { key: 'actions', label: '', thStyle: thStyle('10%') },
      ],
      statementFields: [
        {
          key: 'count',
          label: 'STT',
          thStyle: thStyle('5%'),
          tdClass: 'text-center',
        },
        { key: 'employeeCode', label: 'Mã NV', thStyle: thStyle('9%') },
        { key: 'employeeName', label: 'Tên nhân viên', thStyle: thStyle('20%') },
        { key: 'jobTitleName', label: 'Chức vụ', thStyle: thStyle('14%') },
        {
          key: 'commissionAmount',
          label: 'Hoa hồng',
          thStyle: thStyle('13%'),
          tdClass: 'text-right',
        },
        {
          key: 'adhocAmount',
          label: 'AdHoc',
          thStyle: thStyle('10%'),
          tdClass: 'text-right',
        },
        {
          key: 'bonusAmount',
          label: 'Cộng khác',
          thStyle: thStyle('10%'),
          tdClass: 'text-right',
        },
        {
          key: 'minusAmount',
          label: 'Trừ khác',
          thStyle: thStyle('9%'),
          tdClass: 'text-right',
        },
        {
          key: 'totalAmount',
          label: 'Tổng thưởng',
          thStyle: thStyle('10%'),
          tdClass: 'text-right',
        },
      ],
    };
  },
  computed: {
    isStore() {
      return Number(this.filter.type) === TYPE_STORE;
    },
  },
  created() {
    this.fetchStores();
    this.fetchDepartments();
    this.fetchHistory();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hoa hồng', route: '/commission/categories' },
      { title: 'Chốt sổ hoa hồng' },
    ]);
  },
  methods: {
    onTypeChange() {
      this.status = null;
      this.filter.storeId = null;
      this.filter.storeName = '';
      this.filter.departmentId = null;
      this.componentKey += 1;
    },
    fetchStores() {
      ApiService.setHeader();
      ApiService.query('stores', { params: { limit: 1000, page: 1 } })
        .then(({ data }) => {
          this.stores = (data.data.list_store || []).map((store) => ({
            id: store.id,
            name: `${store.shortName} (${store.name})`,
            suggestionName: `${store.shortName} (${store.name})`,
          }));
          this.filteredStores = [...this.stores];
        })
        .catch(() => makeToastFaile('Không tải được danh sách cửa hàng!'));
    },
    fetchDepartments() {
      ApiService.setHeader();
      ApiService.query('employees/departments')
        .then(({ data }) => {
          this.departments = data.data || [];
        })
        .catch(() => makeToastFaile('Không tải được danh sách bộ phận!'));
    },
    onStoreChange(text) {
      this.filter.storeName = text;
      if (!text) {
        this.filter.storeId = null;
        this.filteredStores = [...this.stores];
        return;
      }
      const keyword = removeAccents(text).toLowerCase();
      this.filteredStores = this.stores.filter(
        (row) => removeAccents(row.name).toLowerCase().indexOf(keyword) > -1,
      );
    },
    onSelectStore(option) {
      this.filter.storeId = option.item.id;
      this.filter.storeName = option.item.name;
    },

    /** Tham số chung cho mọi lời gọi — gom một chỗ để không lệch giữa các hàm */
    buildParams() {
      return {
        type: this.isStore ? 'store' : 'cr',
        storeId: this.isStore ? this.filter.storeId : undefined,
        departmentId: this.isStore ? undefined : this.filter.departmentId,
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
      };
    },
    validate() {
      if (this.isStore && !this.filter.storeId) {
        makeToastFaile('Vui lòng chọn cửa hàng');
        return false;
      }
      if (!this.isStore && !this.filter.departmentId) {
        makeToastFaile('Vui lòng chọn bộ phận');
        return false;
      }
      const message = this.validateDateRange(
        this.filter.fromDate,
        this.filter.toDate,
      );
      if (message) {
        makeToastFaile(message);
        return false;
      }
      return true;
    },

    checkStatus() {
      if (!this.validate()) {
        return;
      }
      this.onLoading = true;
      this.status = null;
      ApiService.setHeader();
      ApiService.query('commission-settlements/status', { params: this.buildParams() })
        .then(({ data }) => {
          this.status = data.data;
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không kiểm tra được trạng thái kỳ'),
          );
        })
        .finally(() => {
          this.onLoading = false;
        });
    },

    confirmSettle() {
      const isResettle = this.status && this.status.isSettled;
      Swal.fire({
        title: isResettle ? 'Chốt lại kỳ này?' : 'Chốt sổ kỳ này?',
        text: isResettle
          ? 'Bản chốt cũ sẽ bị GHI ĐÈ hoàn toàn bằng số liệu mới nhất.'
          : 'Sau khi chốt, bút toán cộng/trừ thuộc kỳ sẽ bị khoá.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3699FF',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: isResettle ? 'Chốt lại' : 'Chốt sổ',
        cancelButtonText: 'Huỷ',
      }).then((result) => {
        if (result.value) {
          this.settle();
        }
      });
    },
    settle() {
      this.isWorking = true;
      ApiService.setHeader();
      ApiService.post('commission-settlements', this.buildParams())
        .then(({ data }) => {
          makeToastSuccess(data.message);
          this.checkStatus();
          this.fetchHistory();
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không chốt sổ được'),
          );
        })
        .finally(() => {
          this.isWorking = false;
        });
    },

    confirmRevoke(id) {
      Swal.fire({
        title: 'Huỷ chốt kỳ này?',
        text: 'Kỳ sẽ được mở khoá, bút toán cộng/trừ sửa được trở lại.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Huỷ chốt',
        cancelButtonText: 'Đóng',
      }).then((result) => {
        if (result.value) {
          this.revoke(id);
        }
      });
    },
    revoke(id) {
      this.isWorking = true;
      ApiService.setHeader();
      ApiService.delete(`commission-settlements/${id}`)
        .then(({ data }) => {
          makeToastSuccess(data.message);
          this.checkStatus();
          this.fetchHistory();
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không huỷ chốt được'),
          );
        })
        .finally(() => {
          this.isWorking = false;
        });
    },

    fetchHistory() {
      this.onLoadingHistory = true;
      ApiService.setHeader();
      ApiService.query('commission-settlements', { params: { limit: 50 } })
        .then(({ data }) => {
          this.history = data.data.items || [];
        })
        .catch(() => {
          this.history = [];
        })
        .finally(() => {
          this.onLoadingHistory = false;
        });
    },

    openStatement(id) {
      this.statement = null;
      this.statementVisible = true;
      this.statementLoading = true;
      ApiService.setHeader();
      ApiService.query(`commission-settlements/${id}/statement`)
        .then(({ data }) => {
          this.statement = data.data;
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được bảng kê'),
          );
        })
        .finally(() => {
          this.statementLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.form-label {
  display: block;
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
}
.hint {
  font-size: 13px;
  border: 1px dashed #d6d9e0;
}
.status-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 16px;
}
.status-box--locked {
  background: #fff4de;
  border-color: #ffe2a8;
}
.status-box--open {
  background: #f1faff;
  border-color: #cfe7fb;
}
.status-box__title {
  font-size: 15px;
  font-weight: 600;
  color: #181c32;
}
.status-box__meta {
  font-size: 13px;
  color: #7e8299;
  margin-top: 4px;
}
.statement-head {
  background: #f8f9fb;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.9;
}
.statement-total td {
  background: #f3f6f9;
  font-weight: 700;
  color: #181c32;
}
</style>
